import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useError } from '../../contexts/ErrorContext';
import ReceivablesLayout from './ReceivablesLayout';
import PlatformButton from '../PlatformButton';
import PlatformInput from '../PlatformInput';
import PlatformText from '../PlatformText';
import LoadingScreen from '../LoadingScreen';
import { useReceivables, RECEIVABLE_ERRORS } from './hooks/useReceivables';
import { useSimplifiedSecurity } from '../../hooks/useSimplifiedSecurity';
import { useTranslation } from '../../hooks/useTranslation';
import { normalizePartyNameForDisplay } from '../../utils/platformUtils';
import { formatDate, formatMoney, roundMoney } from './receivablesUtils';

const RecordPaymentPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation('receivables');
  const { showErrorToast } = useError();
  const { canManageReceivables } = useSimplifiedSecurity();
  const { getReceivable, recordPayment } = useReceivables('none');

  const [sale, setSale] = useState(null);
  const [loading, setLoading] = useState(true);
  const [amount, setAmount] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let active = true;
    getReceivable(id)
      .then((item) => {
        if (active) setSale(item);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [id, getReceivable]);

  const outstanding = sale ? roundMoney(sale.amountOutstanding) : 0;
  const afterPayment = useMemo(() => {
    const numericAmount = Number(amount) || 0;
    return roundMoney(Math.max(outstanding - numericAmount, 0));
  }, [amount, outstanding]);

  const handleSubmit = useCallback(async () => {
    const numericAmount = Number(amount);
    if (!amount || isNaN(numericAmount) || numericAmount <= 0) {
      setFormError(t('errors.amountPositive'));
      return;
    }
    if (roundMoney(numericAmount) > outstanding) {
      setFormError(t('errors.amountExceedsOutstanding'));
      return;
    }

    setSubmitting(true);
    setFormError('');
    try {
      await recordPayment(id, numericAmount);
      showErrorToast(t('messages.paymentRecorded'), { variant: 'info', autoHide: true, duration: 3000 });
      navigate('/receivables');
    } catch (submitError) {
      setFormError(
        submitError.message === RECEIVABLE_ERRORS.AMOUNT_EXCEEDS_OUTSTANDING
          ? t('errors.amountExceedsOutstanding')
          : submitError.message === RECEIVABLE_ERRORS.PERMISSION_DENIED
            ? t('errors.permissionDenied')
            : t('errors.saveFailed')
      );
    } finally {
      setSubmitting(false);
    }
  }, [amount, outstanding, recordPayment, id, showErrorToast, navigate, t]);

  if (!canManageReceivables) {
    return (
      <ReceivablesLayout title={t('payment.title')} subtitle={t('payment.subtitle')} backTo="/receivables">
        <div className="receivables-empty">
          <div className="receivables-empty-icon">🔒</div>
          <PlatformText variant="body">{t('errors.accessDenied')}</PlatformText>
        </div>
      </ReceivablesLayout>
    );
  }

  if (loading) {
    return <LoadingScreen message={t('payment.title')} variant="fullscreen" />;
  }

  if (!sale) {
    return (
      <ReceivablesLayout title={t('payment.title')} subtitle={t('payment.subtitle')} backTo="/receivables">
        <div className="receivables-empty">
          <PlatformText variant="body">{t('errors.saveFailed')}</PlatformText>
        </div>
      </ReceivablesLayout>
    );
  }

  return (
    <ReceivablesLayout title={t('payment.title')} subtitle={t('payment.subtitle')} backTo="/receivables">
      <div className="receivables-form-card">
      <div className="receivables-form">
        <div className="receivables-preview">
          <div className="receivables-preview-row">
            <span>{t('fields.customer')}</span>
            <strong>{normalizePartyNameForDisplay(sale.customerName) || sale.customerName}</strong>
          </div>
          {sale.orderNumber ? (
            <div className="receivables-preview-row">
              <span>{t('fields.orderNumber')}</span>
              <strong>#{sale.orderNumber}</strong>
            </div>
          ) : null}
          <div className="receivables-preview-row">
            <span>{t('fields.invoiceAmount')}</span>
            <strong>{formatMoney(sale.invoiceAmount)}</strong>
          </div>
          <div className="receivables-preview-row">
            <span>{t('payment.alreadyReceived')}</span>
            <strong>{formatMoney(sale.amountReceived)}</strong>
          </div>
          <div className="receivables-preview-row">
            <span>{t('payment.outstanding')}</span>
            <strong>{formatMoney(outstanding)}</strong>
          </div>
          <div className="receivables-preview-row">
            <span>{t('fields.dueDate')}</span>
            <strong>{formatDate(sale.dueDate)}</strong>
          </div>
          <div className="receivables-preview-row">
            <span>{t('payment.afterEntry')}</span>
            <strong>{formatMoney(afterPayment)}</strong>
          </div>
        </div>

        <div className="receivables-field">
          <label htmlFor="payment-amount">{t('fields.amountReceived')}</label>
          <PlatformInput
            id="payment-amount"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
          />
          <span className="receivables-field-hint">{t('payment.amountHelp')}</span>
        </div>

        {formError && <div className="receivables-notice">{formError}</div>}

        <div className="receivables-form-actions">
          <PlatformButton onClick={handleSubmit} disabled={submitting || outstanding <= 0}>
            {submitting ? t('payment.submitting') : t('payment.submit')}
          </PlatformButton>
          <PlatformButton variant="outline" onClick={() => navigate('/receivables')} disabled={submitting}>
            {t('actions.cancel')}
          </PlatformButton>
        </div>
      </div>
      </div>
    </ReceivablesLayout>
  );
};

export default RecordPaymentPage;
